import React, { useEffect, useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import { NoteDTO, NoteTypeDTO, UserDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation';
import MemberDetails from '../common/MemberDetails';
import FileUpload from '../common/FileUpload';
import { useFileApi } from '../../hooks/useFileApi';
import ImageLink from '../common/ImageLink';
import { useUserApi } from '../../hooks/useUserApi';
import { useNoteTypeApi } from '../../hooks/useNoteTypeApi';
import { FileTypeConstants } from '../../constants/FileTypeConstants';

interface NoteFormProps {
  initialValues: NoteDTO;
  onSubmit: (data: NoteDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const FORM_SECTIONS = {
  memberDetails: {
    id: 'member-details',
    title: 'Member Details',
  },
  details: {
    id: 'details',
    title: 'Details',
  },
} as const;

const NoteForm: React.FC<NoteFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<NoteDTO>(initialValues);
  const [admins, setAdmins] = useState<UserDTO[]>([]);
  const [noteTypes, setNoteTypes] = useState<NoteTypeDTO[]>([]);

  const [uploadingFiles, setUploadingFiles] = useState<{
    [key: string]: boolean;
  }>({});

  const { uploadFile, getFileUrl } = useFileApi();
  const { search: searchAdmins, isLoading: isLoadingAdmins } = useUserApi();
  const { search: searchNoteTypes, isLoading: isLoadingNoteTypes } = useNoteTypeApi();

  const validationRules: ValidationRules = {
    description: [{ required: true, message: 'Description is required' }],
    noteTypeId: [{ required: true, message: 'Type is required' }],
  };

  const { errors, validate } = useFormValidation<NoteDTO>(validationRules);

  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const response = await searchAdmins({
          filters: {
            role: 'Admin',
          },
          page: 1,
          pageSize: 999,
          sortBy: 'FirstName',
        });
        setAdmins(response.data?.results || []);
      } catch (error) {
        console.log('Error fetching admins: ', error);
      }
    };
    const fetchNoteTypes = async () => {
      try {
        const response = await searchNoteTypes({
          page: 1,
          pageSize: 999,
          sortBy: 'DisplayPosition',
        });
        setNoteTypes(response?.data?.results || []);
      } catch (error) {
        console.log('Error fetching note types: ', error);
      }
    };

    fetchAdmins();
    fetchNoteTypes();
  }, []);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const handleFileChange =
    (field: keyof NoteDTO) => async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        try {
          setUploadingFiles((prev) => ({ ...prev, [field]: true }));
          const response = await uploadFile('files', file.name, file, 'notes');
          if (response && response.success && response.data) {
            setFormData((prev) => ({
              ...prev,
              [field]: response.data,
            }));
          }
        } catch (error) {
          console.error('Error handling file:', error);
        } finally {
          setUploadingFiles((prev) => ({ ...prev, [field]: false }));
        }
      }
    };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel} isSubmitting={isLoading}>
      <FormSection id={FORM_SECTIONS.memberDetails.id} title={FORM_SECTIONS.memberDetails.title}>
        <MemberDetails member={formData.member ?? {}} />
      </FormSection>

      <FormSection id={FORM_SECTIONS.details.id} title={FORM_SECTIONS.details.title} columns={1}>
        <FormField
          id="description"
          label="Note"
          type="textarea"
          value={formData.description || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Description"
          required={true}
          error={errors.description}
        />
        {formData.fileId && <ImageLink fileId={formData.fileId} label="Attachment" />}
        <FileUpload
          id="fileId"
          onChange={handleFileChange('fileId')}
          title="Upload Attachment"
          accept={FileTypeConstants.ALL_SUPPORTED}
          required={false}
          error={errors.fileId}
          isUploading={uploadingFiles['fileId']}
        />
        <FormField
          id="noteTypeId"
          label="Type"
          type="dropdown"
          value={formData.noteTypeId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Note Type"
          required={true}
          error={errors.noteTypeId}
          options={[
            ...noteTypes.map((noteType) => ({
              value: noteType.id,
              label: noteType.title,
            })),
          ]}
          isLoading={isLoadingNoteTypes}
        />
        <FormField
          id="assignedAdminId"
          label="Assigned To"
          type="dropdown"
          value={formData.assignedAdminId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Assigned Admin"
          required={false}
          error={errors.assignedAdminId}
          options={[
            ...admins.map((admin) => ({
              value: admin.id,
              label: `${admin.firstName} ${admin.lastName}`,
            })),
          ]}
          isLoading={isLoadingAdmins}
        />
        <FormField
          id="isResolved"
          label="Is Resolved"
          type="checkbox"
          value={formData.isResolved || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Is Resolved"
          required={false}
          error={errors.isResolved}
          helper='Check to make record as resolved'
        />
      </FormSection>
    </Form>
  );
};

export default NoteForm;
